/**
 * Structured data (JSON-LD) for SEO.
 * Tells Google "this is an HVAC contractor serving London — here's their info."
 *
 * Validates against https://search.google.com/test/rich-results
 */

const SITE_URL = 'https://deepcontrol.co.uk';

// Service areas — must match /areas page
const SERVICE_AREAS = [
  'London', 'Canary Wharf', 'South London', 'Surrey', 'Chiswick',
  "Shepherd's Bush", 'Wimbledon', 'Croydon', 'Bromley', 'Docklands',
];

export const localBusinessSchema = {
  '@context': 'https://schema.org',
  '@type': ['HVACBusiness', 'LocalBusiness'],
  '@id': `${SITE_URL}/#business`,
  name: 'Deep Control',
  alternateName: 'Deep Control HVAC',
  description:
    'Premium air conditioning and HVAC installation, repair and maintenance across Greater London. F-Gas certified, REFCOM compliant.',
  url: SITE_URL,
  telephone: '+44 20 8059 9941',
  email: 'info@deepcontrol.co.uk',
  image: `${SITE_URL}/images/deep-control-van-canary-wharf.jpg`,
  logo: `${SITE_URL}/images/deep-control-logo.png`,
  priceRange: '££',
  currenciesAccepted: 'GBP',
  paymentAccepted: 'Cash, Credit Card, Bank Transfer',
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'London',
    addressRegion: 'Greater London',
    addressCountry: 'GB',
  },
  areaServed: SERVICE_AREAS.map((name) => ({ '@type': 'City', name })),
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 51.5074,
    longitude: -0.1278,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '08:00',
      closes: '18:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Sunday',
      opens: '00:00',
      closes: '23:59',
      description: 'Emergency call-outs only',
    },
  ],
  hasCredential: [
    { '@type': 'EducationalOccupationalCredential', name: 'F-Gas Certified' },
    { '@type': 'EducationalOccupationalCredential', name: 'REFCOM Compliant' },
  ],
  knowsAbout: [
    'Air conditioning installation',
    'HVAC installation',
    'VRV / VRF systems',
    'Multi-split systems',
    'Ceiling cassette installation',
    'Ducted air conditioning',
    'Commercial HVAC',
    'Air conditioning repair',
    'F-Gas servicing',
    'Refrigerant regassing',
  ],
  brand: [
    'Daikin', 'Mitsubishi Electric', 'Fujitsu', 'Panasonic', 'Toshiba',
    'Samsung', 'LG', 'Bosch', 'Midea', 'Hisense',
  ].map((b) => ({ '@type': 'Brand', name: b })),
  sameAs: [],  // Add Facebook / Instagram / LinkedIn here once available
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+44 20 8059 9941',
    contactType: 'customer service',
    areaServed: 'GB',
    availableLanguage: ['en'],
  },
};

// Service-specific schema (used on /services and /commercial)
export const serviceSchema = (serviceName, description) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: serviceName,
  provider: { '@id': `${SITE_URL}/#business` },
  areaServed: SERVICE_AREAS.map((name) => ({ '@type': 'City', name })),
  description,
});

// Breadcrumb schema for non-home pages
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE_URL}${it.path}`,
  })),
});

// FAQ schema (helps with PAA boxes in Google)
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((q) => ({
    '@type': 'Question',
    name: q.q,
    acceptedAnswer: { '@type': 'Answer', text: q.a },
  })),
});
